import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { toast } from '@codes/ui-kit/components/ui/use-toast';

import type { EditorNotificationEventData, EditorNotificationEventType } from '../types';

export const useEditorNotifications = () => {
    const { t } = useTranslation();

    const notify = useCallback(
        <T extends EditorNotificationEventType>(eventType: T, data: EditorNotificationEventData<T>) => {
            switch (eventType) {
                case 'load-error':
                    toast({
                        variant: 'destructive',
                        title: t('box:editorPage.error.title'),
                        description: t('box:editorPage.load.error'),
                    });
                    console.error('Load failed:', (data as any).error);
                    break;

                case 'save-success':
                    toast({
                        title: t('box:editorPage.save.complete'),
                        description: t('box:editorPage.save.success'),
                    });
                    break;

                case 'save-error':
                    toast({
                        variant: 'destructive',
                        title: t('box:editorPage.save.failed'),
                        description: t('box:editorPage.save.error'),
                    });
                    console.error('Save failed:', (data as any).error);
                    break;

                case 'export-success':
                    toast({
                        title: t('box:editorPage.export.complete'),
                        description: t('box:editorPage.export.success'),
                    });
                    break;

                case 'export-error':
                    toast({
                        variant: 'destructive',
                        title: t('box:editorPage.export.failed'),
                        description: t('box:editorPage.export.error'),
                    });
                    console.error('Export failed:', (data as any).error);
                    break;
            }
        },
        [t]
    );

    const notifyLoadError = useCallback(
        (error: Error) => {
            notify('load-error', { error });
        },
        [notify]
    );

    const notifySaveSuccess = useCallback(
        (title?: string) => {
            notify('save-success', { title });
        },
        [notify]
    );

    const notifySaveError = useCallback(
        (error: unknown) => {
            notify('save-error', { error });
        },
        [notify]
    );

    return {
        notify,
        notifyLoadError,
        notifySaveSuccess,
        notifySaveError,
    };
};
